import React, { useState } from 'react'
import {
    Drawer,
    IconButton,
    Theme,
    Typography,
    FormControlLabel,
    Switch,
    Checkbox,
    FormGroup,
    Radio,
    RadioGroup,
} from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import {
    EntityClass,
    CriteriaGroup,
    ResidentTermDirectoryStatus,
} from '../../../../models'
import { AuditFilterSection, MessageFilterSection } from './AuditFilterSection'

import { FilterValue } from './types'

interface AuditFilterDrawerProps {
    open: boolean
    onClose: () => void
    theme: Theme
    drawerWidth: number
    selectedFilters: Record<number, FilterValue>
    onFilterChange: (filters: Record<number, FilterValue>) => void
    entityClassifications: EntityClass[]
    criteriaGroup?: CriteriaGroup | null
    isLoading: boolean
    hasMessagesFilter: boolean
    onHasMessagesFilterChange: (value: boolean) => void
    statusFilter: ResidentTermDirectoryStatus[]
    onStatusFilterChange: (statuses: ResidentTermDirectoryStatus[]) => void
    onClearAll: () => void
    hasActiveFilters: boolean
    timePeriodFilter: 'current' | 'future' | 'past' | 'all'
    onTimePeriodFilterChange: (
        period: 'current' | 'future' | 'past' | 'all',
    ) => void
}

const StatusFilterSection: React.FC<{
    selectedStatuses: ResidentTermDirectoryStatus[]
    onStatusChange: (statuses: ResidentTermDirectoryStatus[]) => void
    theme: Theme
}> = ({ selectedStatuses, onStatusChange, theme }) => {
    const statusOptions: {
        value: ResidentTermDirectoryStatus
        label: string
    }[] = [
        { value: 'MATCHED', label: 'Matched' },
        { value: 'MISSING_RENT_ROLL', label: 'Missing Rent Roll' },
        { value: 'MISSING_LEASE', label: 'Missing Lease' },
    ]

    const handleStatusChange = (status: ResidentTermDirectoryStatus) => {
        if (selectedStatuses.includes(status)) {
            onStatusChange(selectedStatuses.filter((s) => s !== status))
        } else {
            onStatusChange([...selectedStatuses, status])
        }
    }

    return (
        <div style={{ marginBottom: theme.spacing(3) }}>
            <Typography
                variant="subtitle1"
                style={{
                    marginBottom: theme.spacing(1),
                    fontWeight: 'bold',
                }}
            >
                Status Filter
            </Typography>

            <FormGroup>
                {statusOptions.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        control={
                            <Checkbox
                                checked={selectedStatuses.includes(
                                    option.value,
                                )}
                                onChange={() =>
                                    handleStatusChange(option.value)
                                }
                                color="primary"
                            />
                        }
                        label={option.label}
                    />
                ))}
            </FormGroup>
        </div>
    )
}

// Add a component for time period filtering
const TimePeriodFilterSection: React.FC<{
    selectedPeriod: 'current' | 'future' | 'past' | 'all'
    onPeriodChange: (period: 'current' | 'future' | 'past' | 'all') => void
    theme: Theme
}> = ({ selectedPeriod, onPeriodChange, theme }) => {
    const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onPeriodChange(
            event.target.value as 'current' | 'future' | 'past' | 'all',
        )
    }

    return (
        <div style={{ marginBottom: theme.spacing(3) }}>
            <Typography
                variant="subtitle1"
                style={{
                    marginBottom: theme.spacing(1),
                    fontWeight: 'bold',
                }}
            >
                Lease Time Period
            </Typography>

            <RadioGroup
                aria-label="lease-time-period"
                name="lease-time-period"
                value={selectedPeriod}
                onChange={handlePeriodChange}
            >
                <FormControlLabel
                    value="current"
                    control={<Radio color="primary" />}
                    label="Current"
                />
                <FormControlLabel
                    value="future"
                    control={<Radio color="primary" />}
                    label="Future"
                />
                <FormControlLabel
                    value="past"
                    control={<Radio color="primary" />}
                    label="Past"
                />
                <FormControlLabel
                    value="all"
                    control={<Radio color="primary" />}
                    label="All"
                />
            </RadioGroup>
        </div>
    )
}

export const AuditFilterDrawer: React.FC<AuditFilterDrawerProps> = ({
    open,
    onClose,
    theme,
    drawerWidth,
    selectedFilters,
    onFilterChange,
    entityClassifications,
    criteriaGroup,
    isLoading,
    hasMessagesFilter,
    onHasMessagesFilterChange,
    statusFilter,
    onStatusFilterChange,
    onClearAll,
    hasActiveFilters,
    timePeriodFilter,
    onTimePeriodFilterChange,
}) => {
    const handleFilterChange = (
        classification: EntityClass,
        updates: Partial<FilterValue>,
    ) => {
        const currentFilter = selectedFilters[classification.id] || {
            id: classification.id,
            enabled: false,
        }
        onFilterChange({
            ...selectedFilters,
            [classification.id]: {
                ...currentFilter,
                ...updates,
                enabled: Boolean(updates.min || updates.max),
            },
        })
    }

    const filteredClassifications = entityClassifications.filter(
        (classification) =>
            // Don't show lease_start and lease_end filters since we're using the radio group instead
            classification.name !== 'lease_start' &&
            classification.name !== 'lease_end' &&
            (classification.always_display ||
                criteriaGroup?.match_criteria.some(
                    (criteria) =>
                        criteria.entity_class.id === classification.id &&
                        (criteria.match_type === 'ANY_REQUIRED' ||
                            criteria.match_type === 'SIGNED_REQUIRED') &&
                        criteria.display,
                )),
    )

    const handleTimePeriodFilterChange = (
        period: 'current' | 'future' | 'past' | 'all',
    ) => {
        onTimePeriodFilterChange(period)
    }

    return (
        <Drawer
            variant="persistent"
            anchor="right"
            open={open}
            PaperProps={{
                style: {
                    width: drawerWidth,
                    marginTop: '58px',
                    height: 'calc(100% - 58px)',
                },
            }}
        >
            <div
                style={{
                    padding: theme.spacing(2),
                    height: '100%',
                    overflow: 'auto',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Typography variant="h6">Filters</Typography>
                        {hasActiveFilters && (
                            <button
                                onClick={onClearAll}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                    padding: theme.spacing(1),
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                    <IconButton onClick={onClose} size="small">
                        <ChevronRightIcon />
                    </IconButton>
                </div>

                {/* Add Time Period Filter Section */}
                <TimePeriodFilterSection
                    selectedPeriod={timePeriodFilter}
                    onPeriodChange={handleTimePeriodFilterChange}
                    theme={theme}
                />

                {/* Add Status Filter Section */}
                <StatusFilterSection
                    selectedStatuses={statusFilter}
                    onStatusChange={onStatusFilterChange}
                    theme={theme}
                />

                <MessageFilterSection
                    hasMessagesFilter={hasMessagesFilter}
                    onHasMessagesFilterChange={onHasMessagesFilterChange}
                />

                {isLoading ? (
                    <div>Loading...</div>
                ) : (
                    filteredClassifications.map((classification) => {
                        const filter = selectedFilters[classification.id] || {
                            enabled: false,
                        }

                        return (
                            <AuditFilterSection
                                key={classification.id}
                                classification={classification}
                                filter={filter}
                                onFilterChange={handleFilterChange}
                            />
                        )
                    })
                )}
            </div>
        </Drawer>
    )
}
